"use client";

import { graphql, readFragment, useMutation } from "@opensea/graphql";
import { useToasts } from "@opensea/ui-kit";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useAuthenticatedCallback } from "@/hooks/use-authenticated-callback";
import { Sound, usePlaySound } from "@/hooks/use-play-sound";
import { useToggleWatchTokenFragment } from "./use-toggle-watch-token.fragment";
const watchTokenMutation = graphql(`
  mutation watchTokenMutation($contract: ContractIdentifierInput!) {
    watchToken(contract: $contract) {
      success
      error {
        __typename
      }
    }
  }
`);
const unwatchTokenMutation = graphql(`
  mutation unwatchTokenMutation($contract: ContractIdentifierInput!) {
    unwatchToken(contract: $contract) {
      success
      error {
        __typename
      }
    }
  }
`);
export function useToggleWatchToken({
  currency: currencyKey,
  isWatching: isWatchingInitially
}) {
  const t = useTranslations("useToggleWatchToken");
  const currency = readFragment(useToggleWatchTokenFragment, currencyKey);
  const {
    contractAddress,
    chainIdentifier,
    name,
    symbol
  } = currency;
  const [isWatching, setIsWatching] = useState(isWatchingInitially);
  useEffect(() => {
    setIsWatching(isWatchingInitially);
  }, [isWatchingInitially]);
  const [playAddWatchlist] = usePlaySound(Sound.WatchlistAdd);
  const [playRemoveWatchlist] = usePlaySound(Sound.WatchlistRemove);
  const [{
    fetching: watchTokenFetching
  }, watchToken] = useMutation(watchTokenMutation);
  const [{
    fetching: unwatchTokenFetching
  }, unwatchToken] = useMutation(unwatchTokenMutation);
  const {
    showErrorMessage,
    showSuccessMessage
  } = useToasts();
  const sendMutation = useDebouncedCallback(async watch => {
    try {
      let error;
      const contract = {
        address: contractAddress,
        chain: chainIdentifier
      };
      if (watch) {
        const result = await watchToken({
          contract
        });
        error = result.data?.watchToken?.error?.__typename;
        if (!error) {
          showSuccessMessage(t("addedToWatchlist", {
            name: name || symbol
          }));
          playAddWatchlist();
        }
      } else {
        const result_0 = await unwatchToken({
          contract
        });
        error = result_0.data?.unwatchToken?.error?.__typename;
        if (!error) {
          showSuccessMessage(t("removedFromWatchlist", {
            name: name || symbol
          }));
          playRemoveWatchlist();
        }
      }
      if (error) {
        let errorMessage_0 = t("errorGeneric");
        if (error === "EntryNotFoundError") {
          errorMessage_0 = t("errorEntryNotFound");
        } else if (error === "MaxWatchListSizeError") {
          errorMessage_0 = t("errorMaxWatchListSize");
        }
        showErrorMessage(errorMessage_0, {
          duration: 10_000
        });
        setIsWatching(prev_0 => !prev_0);
      }
    } catch {
      const errorMessage = t("errorGeneric");
      showErrorMessage(errorMessage, {
        duration: 10_000
      });
      setIsWatching(prev => !prev);
    }
  }, 750);
  const execute = useAuthenticatedCallback(async () => {
    const watch_0 = !isWatching;
    setIsWatching(watch_0);
    await sendMutation(watch_0);
  });
  return {
    execute,
    fetching: watchTokenFetching || unwatchTokenFetching,
    isWatching
  };
}